// ============================================================
// FINAL DECISION DOSSIER (#8)
// Consolidates everything gathered on the candidate onto one screen
// so the recruiter can make an informed decision:
//  - Application answers + tags
//  - Phone screen notes
//  - Interview scorecard (% + interpretation + per-competency breakdown)
//  - Paid trial (result + checklist pass-rate + payment)
//  - Reference check (result + would-rehire + concerns)
//  - Auto-computed readiness checklist
//  - Decision + reason + approver
// ============================================================
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Star, ClipboardCheck, FileCheck, FileText, User,
  CheckCircle2, AlertTriangle, XCircle, Phone, Award,
} from 'lucide-react';
import {
  api, SCORE_FIELDS, TRIAL_CHECKS, FINAL_DECISIONS, ANSWER_TAGS, TAG_COLORS, fmtSaved, fmtEventDate,
} from './constants';

const MANUAL_CHECKS = [
  { key: 'employment_type_confirmed', label: 'Employment type confirmed' },
  { key: 'start_date_confirmed', label: 'Start date confirmed' },
  { key: 'casual_trial_explained', label: 'Casual trial pathway explained' },
  { key: 'access_level_approved', label: 'Access level approved' },
  { key: 'manager_assigned', label: 'Manager assigned' },
  { key: 'training_pathway_assigned', label: 'Training pathway assigned' },
];

const Section = ({ icon: Icon, title, status, children }) => (
  <div className="ps-card-static p-5 mb-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-primary" />
      <h3 className="text-sm font-bold flex-1">{title}</h3>
      {status}
    </div>
    {children}
  </div>
);

const NotDone = ({ text }) => <p className="text-xs text-muted-foreground italic">{text}</p>;

export function DecisionForm({ candidateId, navigate }) {
  const [candidate, setCandidate] = useState(null);
  const [form, setForm] = useState({ decision: '', decision_reason: '', approved_by: '' });
  const [manual, setManual] = useState(MANUAL_CHECKS.reduce((a, c) => ({ ...a, [c.key]: false }), {}));
  const [existingId, setExistingId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api(`/api/recruitment/candidates/${candidateId}`).then(c => {
      if (!active) return;
      setCandidate(c);
      const d = c.hiring_decision;
      if (d) {
        setForm({ decision: d.decision || '', decision_reason: d.decision_reason || '', approved_by: d.approved_by || '' });
        setManual(MANUAL_CHECKS.reduce((a, m) => ({ ...a, [m.key]: !!d[m.key] }), {}));
        setExistingId(d.id);
        setSavedAt(d.updated_at || d.created_at);
      }
    }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [candidateId]);

  if (loading || !candidate) return <div className="h-40 bg-muted rounded-2xl animate-skeleton" />;

  const scorecard = candidate.scorecards?.[0];
  const trial = candidate.paid_trials?.[0];
  const ref = candidate.reference_checks?.[0];
  const answers = candidate.application_answers || {};
  const answerEntries = Object.entries(answers);

  // ---- Auto-computed readiness ----
  const auto = {
    application_reviewed: answerEntries.length > 0,
    interview_completed: !!scorecard,
    scorecard_completed: !!scorecard,
    paid_trial_completed: !!trial,
    reference_check_completed: !!ref,
  };
  const AUTO_LABELS = {
    application_reviewed: 'Application reviewed',
    interview_completed: 'Interview completed',
    scorecard_completed: 'Interview scorecard completed',
    paid_trial_completed: 'Paid trial completed',
    reference_check_completed: 'Reference check completed',
  };

  // ---- Tag summary ----
  const tagCounts = ANSWER_TAGS.reduce((a, t) => ({ ...a, [t]: 0 }), {});
  answerEntries.forEach(([, v]) => { if (typeof v === 'object' && v.tag && tagCounts[v.tag] !== undefined) tagCounts[v.tag]++; });

  // ---- Trial checklist pass-rate ----
  const trialPass = trial ? TRIAL_CHECKS.filter(t => trial[t.key] === true).length : 0;
  const trialFail = trial ? TRIAL_CHECKS.filter(t => trial[t.key] === false).length : 0;

  const scorePct = scorecard ? scorecard.weighted_total_score : 0;
  const scoreColor = scorePct >= 90 ? 'badge-active' : scorePct >= 75 ? 'badge-improving' : scorePct >= 60 ? 'badge-draft' : 'badge-critical';

  const resultBadge = (result, positiveWords = ['Strong', 'hire', 'Acceptable', 'Possible']) => {
    if (!result) return null;
    const good = positiveWords.some(w => result.toLowerCase().includes(w.toLowerCase()));
    const bad = /do not|concern/i.test(result);
    const cls = bad ? 'badge-critical' : good ? 'badge-active' : 'badge-draft';
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>{result}</span>;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { candidate_id: candidateId, ...auto, ...manual, decision: form.decision, decision_reason: form.decision_reason, approved_by: form.approved_by };
      if (existingId) {
        const res = await api(`/api/recruitment/decisions/${existingId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        setSavedAt(res.updated_at || new Date().toISOString());
      } else {
        const res = await api('/api/recruitment/decisions', { method: 'POST', body: JSON.stringify(payload) });
        setExistingId(res.id);
        setSavedAt(res.updated_at || res.created_at || new Date().toISOString());
      }
      toast.success('Decision saved');
      navigate('candidate', candidateId);
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div data-testid="decision-form">
      <button className="ch-back" onClick={() => navigate('candidate', candidateId)}><ArrowLeft size={16} /> Back</button>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="text-xl font-bold">Final Decision Dossier</h1>
        <span className="text-xs text-muted-foreground font-mono">{candidate.candidate_number}</span>
      </div>
      <p className="text-sm text-muted-foreground mb-1">{candidate.candidate_name}{candidate.vacancy_role_title ? ` · ${candidate.vacancy_role_title}` : ''}</p>
      {savedAt && <p className="text-xs text-muted-foreground mb-5 flex items-center gap-1"><Check size={12} className="text-[hsl(var(--success))]" /> Last saved {fmtSaved(savedAt)}</p>}
      {!savedAt && <div className="mb-5" />}

      {/* Readiness overview */}
      <div className="ps-card-static p-5 mb-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Readiness</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.keys(auto).map(k => (
            <div key={k} className="flex items-center gap-2 text-sm" data-testid={`auto-check-${k}`}>
              {auto[k] ? <CheckCircle2 size={16} className="text-[hsl(var(--success))]" /> : <XCircle size={16} className="text-muted-foreground/50" />}
              <span className={auto[k] ? '' : 'text-muted-foreground'}>{AUTO_LABELS[k]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Application */}
      <Section icon={FileText} title="Application" status={answerEntries.length > 0 ? (
        <div className="flex gap-1 flex-wrap">{ANSWER_TAGS.filter(t => tagCounts[t] > 0).map(t => <span key={t} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TAG_COLORS[t]}`}>{tagCounts[t]} {t}</span>)}</div>
      ) : null}>
        {answerEntries.length > 0 ? (
          <div className="space-y-2">
            {answerEntries.map(([q, v], i) => (
              <div key={i} className="flex items-start gap-2 text-xs border-b border-border/30 pb-2 last:border-0">
                <span className="flex-1"><span className="font-semibold text-muted-foreground">Q{i + 1}.</span> {typeof v === 'object' ? v.answer : v}</span>
                {typeof v === 'object' && v.tag && <span className={`px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${TAG_COLORS[v.tag] || 'bg-muted'}`}>{v.tag}</span>}
              </div>
            ))}
          </div>
        ) : <NotDone text="No application answers recorded." />}
      </Section>

      {/* Phone screen notes */}
      <Section icon={Phone} title="Phone Screen & Notes">
        {candidate.notes ? <p className="text-xs text-muted-foreground whitespace-pre-wrap">{candidate.notes}</p> : <NotDone text="No phone screen notes recorded." />}
      </Section>

      {/* Interview scorecard */}
      <Section icon={Star} title="Interview Scorecard" status={scorecard ? <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${scoreColor}`}>{scorePct}% · {scorecard.score_interpretation}</span> : null}>
        {scorecard ? (
          <div>
            {scorecard.recommendation && <p className="text-xs mb-3"><span className="font-semibold">Recommendation:</span> {scorecard.recommendation}</p>}
            <div className="space-y-1.5 mb-3">
              {SCORE_FIELDS.map(f => {
                const v = scorecard[f.key] || 0;
                return (
                  <div key={f.key} className="flex items-center gap-3">
                    <span className="text-xs w-40 flex-shrink-0">{f.label}</span>
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(v / 5) * 100}%`, background: v >= 4 ? 'hsl(var(--success))' : v >= 3 ? 'hsl(var(--primary))' : v > 0 ? 'hsl(38 80% 50%)' : 'transparent' }} />
                    </div>
                    <span className="text-xs font-semibold w-8 text-right">{v}/5</span>
                  </div>
                );
              })}
            </div>
            {scorecard.interview_notes && <p className="text-xs text-muted-foreground whitespace-pre-wrap border-t border-border/30 pt-2"><span className="font-semibold">Notes:</span> {scorecard.interview_notes}</p>}
          </div>
        ) : <NotDone text="Interview scorecard not completed." />}
      </Section>

      {/* Paid trial */}
      <Section icon={ClipboardCheck} title="Paid Work Trial" status={trial ? resultBadge(trial.trial_result) : null}>
        {trial ? (
          <div className="text-xs space-y-2">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1"><CheckCircle2 size={13} className="text-[hsl(var(--success))]" /> {trialPass} positive</span>
              {trialFail > 0 && <span className="flex items-center gap-1"><AlertTriangle size={13} className="text-destructive" /> {trialFail} concern{trialFail === 1 ? '' : 's'}</span>}
              <span className="text-muted-foreground">of {TRIAL_CHECKS.length} observations</span>
              {trial.trial_date && <span className="text-muted-foreground">· {fmtEventDate(trial.trial_date)}</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full font-semibold ${trial.paid ? 'badge-active' : 'badge-draft'}`}>{trial.paid ? 'Paid' : 'Payment pending'}</span>
              {trial.payment_amount ? <span className="text-muted-foreground">${trial.payment_amount} {trial.payment_method ? `· ${trial.payment_method}` : ''}</span> : null}
            </div>
            {trial.trial_notes && <p className="text-muted-foreground whitespace-pre-wrap border-t border-border/30 pt-2"><span className="font-semibold">Notes:</span> {trial.trial_notes}</p>}
          </div>
        ) : <NotDone text="Paid trial not completed." />}
      </Section>

      {/* Reference check */}
      <Section icon={FileCheck} title="Reference Check" status={ref ? resultBadge(ref.reference_result) : null}>
        {ref ? (
          <div className="text-xs space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold">{ref.referee_name}</span>
              {ref.relationship_to_candidate && <span className="text-muted-foreground">{ref.relationship_to_candidate}</span>}
              {ref.would_rehire !== null && ref.would_rehire !== undefined && (
                <span className={`px-2 py-0.5 rounded-full font-semibold ${ref.would_rehire ? 'badge-active' : 'badge-critical'}`}>Would rehire: {ref.would_rehire ? 'Yes' : 'No'}</span>
              )}
            </div>
            {ref.concerns && <p className="text-muted-foreground"><span className="font-semibold text-destructive">Concerns:</span> {ref.concerns}</p>}
            {ref.trust_notes && <p className="text-muted-foreground"><span className="font-semibold">Trust:</span> {ref.trust_notes}</p>}
            {ref.client_care_notes && <p className="text-muted-foreground"><span className="font-semibold">Client care:</span> {ref.client_care_notes}</p>}
          </div>
        ) : <NotDone text="Reference check not completed." />}
      </Section>

      {/* Final decision */}
      <div className="ps-card-static p-6 mb-4" style={{ borderColor: 'hsl(var(--primary))', borderWidth: '1.5px' }}>
        <div className="flex items-center gap-2 mb-4"><Award size={18} className="text-primary" /><h3 className="text-sm font-bold">Make the Decision</h3></div>

        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Onboarding confirmations</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
          {MANUAL_CHECKS.map(c => (
            <button key={c.key} onClick={() => setManual(m => ({ ...m, [c.key]: !m[c.key] }))} data-testid={`manual-check-${c.key}`} className="flex items-center gap-2 p-2 rounded-lg text-left text-xs hover:bg-muted/30 transition-colors">
              <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${manual[c.key] ? 'bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white' : 'border-input'}`}>{manual[c.key] && <Check size={12} />}</span>
              <span>{c.label}</span>
            </button>
          ))}
        </div>

        <div className="rec-field"><label className="rec-label">Decision</label>
          <select value={form.decision} onChange={e => setForm(f => ({ ...f, decision: e.target.value }))} className="rec-input" data-testid="decision-select">
            <option value="">Select...</option>{FINAL_DECISIONS.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div className="rec-field mt-4"><label className="rec-label">Reason / rationale</label>
          <textarea value={form.decision_reason} onChange={e => setForm(f => ({ ...f, decision_reason: e.target.value }))} className="rec-input rec-textarea" rows={3} data-testid="decision-reason" />
        </div>
        <div className="rec-field mt-4"><label className="rec-label">Approved by</label>
          <input value={form.approved_by} onChange={e => setForm(f => ({ ...f, approved_by: e.target.value }))} className="rec-input" data-testid="decision-approver" />
        </div>
        <button onClick={save} disabled={saving} className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }} data-testid="decision-save-btn">{saving ? 'Saving...' : 'Save Decision'}</button>
      </div>
    </div>
  );
}
